/**
 * Histórico: execuções concluídas e canceladas, pesquisáveis pelo
 * identificador (ex.: tudo do "522"). Abrir uma mostra o passo a passo com o
 * horário de cada toque, as pausas com motivo e o cancelamento.
 * Exportável pelo botão único de exportação (registry `area-pcp-historico`).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EmptyState, EmptyStateErro } from "../../components/EmptyState";
import BotaoExportacao from "../../components/Exportacao";
import { SoIcone } from "../../components/ListaCompacta/styles";
import { PageLoader } from "../../components/LoadingMarcenaria";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import { lerExecucao, listarHistorico, listarTarefas } from "../../services/areaPcp";
import Casca from "./Casca";
import { ROTA_EXECUCAO } from "./componentes/IniciarExecucao";
import { ROTULO_STATUS, TOM_STATUS, dataHoraLocal, duracao, horaLocal } from "./dominio/formatos";
import { useAreaPcp } from "./offline/AreaPcpContext";
import { Aviso, BotaoTexto, Campo, Corpo, Linha, Lista, Meta, Mono, Selo, Sub, TituloLinha } from "./styles";

export const PAINEL_EXPORTACAO = "area-pcp-historico";
const DIAS_PADRAO = 30;

const ROTULO_EVENTO = {
  iniciou: "Iniciou",
  feito: "Feito",
  desfeito: "Voltou para a fazer",
  pulado: "Pulado",
  pausou: "Pausou",
  retomou: "Retomou",
  cancelou: "Cancelou",
};

function hojeMenos(dias) {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
}

export function montarExportacao(itens) {
  return {
    titulo: "Área do PCP · Histórico de execuções",
    pronto: true,
    resumo: [{ label: "Execuções", value: String(itens.length), type: "text" }],
    secoes: [
      {
        id: "execucoes",
        label: "Execuções concluídas e canceladas",
        columns: [
          { key: "identificador", label: "Identificador", type: "text" },
          { key: "tarefa", label: "Tarefa", type: "text" },
          { key: "status", label: "Situação", type: "text" },
          { key: "inicio", label: "Início", type: "text" },
          { key: "fim", label: "Fim", type: "text" },
          { key: "duracao", label: "Duração", type: "text" },
          { key: "por", label: "Iniciada por", type: "text" },
          { key: "motivo", label: "Motivo do cancelamento", type: "text" },
        ],
        rows: itens.map((e) => ({
          identificador: e.identificador || "—",
          tarefa: e.tarefa.titulo,
          status: ROTULO_STATUS[e.status],
          inicio: dataHoraLocal(e.iniciada_em),
          fim: dataHoraLocal(e.concluida_em || e.cancelada_em),
          duracao: duracao(e.duracao_segundos),
          por: e.iniciada_por || "—",
          motivo: e.motivo_cancelamento || "",
        })),
      },
    ],
  };
}

function Detalhe({ execucaoId, onFechar }) {
  const { request } = useAuth();
  const [detalhe, setDetalhe] = useState(null);
  const [erro, setErro] = useState("");
  useEffect(() => {
    if (!execucaoId) return;
    setDetalhe(null);
    lerExecucao(request, execucaoId).then(setDetalhe).catch((f) => setErro(f.message));
  }, [request, execucaoId]);

  const tituloDoPasso = (chave) => {
    if (!detalhe || !chave) return "";
    const passo = detalhe.roteiro.passos.find((p) => p.chave === chave.split("/")[0]);
    if (!passo) return "";
    const item = passo.itens?.find((i) => i.chave === chave);
    return `Passo ${passo.numero} · ${item ? item.titulo_curto || item.texto : passo.titulo_curto || passo.texto}`;
  };

  return (
    <SlideOverPanel
      open={Boolean(execucaoId)}
      onClose={onFechar}
      title={detalhe?.identificador || detalhe?.tarefa?.titulo || "Execução"}
      subtitle={detalhe ? `${detalhe.tarefa.titulo} · versão ${detalhe.versao}` : ""}
      width={520}
    >
      {erro ? <Aviso $tom="danger">{erro}</Aviso> : null}
      {detalhe ? (
        <Lista as="ol" aria-label="Passo a passo">
          {detalhe.eventos.map((ev) => (
            <Linha key={ev.id}>
              <Corpo>
                <TituloLinha>
                  {horaLocal(ev.ocorrido_em)} · {ROTULO_EVENTO[ev.tipo]}
                </TituloLinha>
                <Sub>{tituloDoPasso(ev.unidade) || ev.motivo || ""}</Sub>
                <Meta>
                  {dataHoraLocal(ev.ocorrido_em)} · {ev.usuario || "—"}
                  {ev.motivo && ev.unidade ? ` · ${ev.motivo}` : ""}
                </Meta>
              </Corpo>
            </Linha>
          ))}
        </Lista>
      ) : null}
    </SlideOverPanel>
  );
}

export default function Historico() {
  const { request } = useAuth();
  const { pacote } = useAreaPcp();
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const [filtros, setFiltros] = useState({ q: "", tarefa: "", status: "todas", de: hojeMenos(DIAS_PADRAO), ate: "" });
  const [tarefas, setTarefas] = useState([]);
  const [estado, setEstado] = useState({ carregando: true, itens: [], erro: null });
  const [aberta, setAberta] = useState(null);

  useEffect(() => {
    listarTarefas(request).then((r) => setTarefas(r.tarefas)).catch(() => setTarefas([]));
  }, [request]);

  useEffect(() => {
    let ativo = true;
    const timer = setTimeout(() => {
      listarHistorico(request, filtros)
        .then((r) => ativo && setEstado({ carregando: false, itens: r.execucoes, erro: null }))
        .catch((erro) => ativo && setEstado({ carregando: false, itens: [], erro }));
    }, 250);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [request, filtros]);

  const exportacao = useMemo(() => montarExportacao(estado.itens), [estado.itens]);
  useExportSnapshot(PAINEL_EXPORTACAO, estado.carregando ? null : exportacao, pageRef);
  const mudar = (campo) => (e) => setFiltros((atual) => ({ ...atual, [campo]: e.target.value }));

  return (
    <Casca
      contagemAbertas={(pacote?.execucoes || []).length}
      acoes={
        <SoIcone>
          <BotaoExportacao titulo={exportacao.titulo} resumo={exportacao.resumo} secoes={exportacao.secoes} label="Exportar" />
        </SoIcone>
      }
    >
      <div ref={pageRef} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
          <Campo>
            <span>Identificador</span>
            <input value={filtros.q} onChange={mudar("q")} placeholder="Ex.: 522" style={{ fontFamily: "var(--font-mono)" }} />
          </Campo>
          <Campo>
            <span>Tarefa</span>
            <select value={filtros.tarefa} onChange={mudar("tarefa")}>
              <option value="">Todas as tarefas</option>
              {tarefas.map((t) => <option key={t.id} value={t.id}>{t.titulo}</option>)}
            </select>
          </Campo>
          <Campo>
            <span>Situação</span>
            <select value={filtros.status} onChange={mudar("status")}>
              <option value="todas">Concluídas e canceladas</option>
              <option value="concluida">Concluídas</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </Campo>
          <Campo>
            <span>De</span>
            <input type="date" value={filtros.de} onChange={mudar("de")} />
          </Campo>
          <Campo>
            <span>Até</span>
            <input type="date" value={filtros.ate} onChange={mudar("ate")} />
          </Campo>
        </div>

        {estado.carregando ? <PageLoader scope="kpis" /> : null}
        {estado.erro ? <EmptyStateErro erro={estado.erro} /> : null}
        {!estado.carregando && !estado.erro && !estado.itens.length ? (
          <EmptyState
            tone="search"
            titulo="Nenhuma execução neste filtro"
            descricao="Concluídas e canceladas aparecem aqui."
            acoes={[{ label: "Limpar filtros", onClick: () => setFiltros({ q: "", tarefa: "", status: "todas", de: "", ate: "" }) }]}
          />
        ) : null}
        {estado.itens.length ? (
          <Lista aria-label="Execuções encerradas">
            {estado.itens.map((e) => (
              <Linha key={e.id} $tom={e.status === "cancelada" ? "danger" : undefined}>
                <Corpo>
                  <TituloLinha>
                    <Mono>{e.identificador || e.tarefa.titulo}</Mono>
                  </TituloLinha>
                  <Sub>{e.tarefa.titulo}</Sub>
                  <Meta>
                    <Selo $tom={TOM_STATUS[e.status]}>{ROTULO_STATUS[e.status]}</Selo>
                    {e.status === "cancelada" ? (
                      <span>
                        no passo {e.passo_atual?.numero ?? "—"} · {e.motivo_cancelamento}
                      </span>
                    ) : null}
                    <span>
                      {dataHoraLocal(e.iniciada_em)} → {dataHoraLocal(e.concluida_em || e.cancelada_em)} · {duracao(e.duracao_segundos)}
                    </span>
                    <span>{e.iniciada_por || "—"}</span>
                  </Meta>
                </Corpo>
                <BotaoTexto type="button" onClick={() => setAberta(e.id)}>
                  Passo a passo
                </BotaoTexto>
                <BotaoTexto type="button" onClick={() => navigate(ROTA_EXECUCAO(e.id))}>
                  Abrir
                </BotaoTexto>
              </Linha>
            ))}
          </Lista>
        ) : null}
      </div>
      <Detalhe execucaoId={aberta} onFechar={() => setAberta(null)} />
    </Casca>
  );
}
